import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/axios';
import useProjectStore from '../stores/projectStore';

export function useProjectReports() {
  const projectId = useProjectStore((s) => s.currentProjectId);
  return useQuery({
    queryKey: ['project-reports', projectId],
    queryFn: async () => {
      const { data } = await api.get('/project-reports', {
        params: projectId ? { project_id: projectId } : {},
        skipProject: true,
      });
      return data.reports || [];
    },
    enabled: !!projectId,
  });
}

export function useProjectReport(id) {
  return useQuery({
    queryKey: ['project-report', id],
    queryFn: async () => {
      const { data } = await api.get(`/project-reports/${id}`, { skipProject: true });
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateProjectReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/project-reports', payload, { skipProject: true });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-reports'] }),
  });
}

export function useRegenerateProjectReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(`/project-reports/${id}/regenerate`, {}, { skipProject: true });
      return data;
    },
    onSuccess: (report) => {
      qc.invalidateQueries({ queryKey: ['project-reports'] });
      qc.invalidateQueries({ queryKey: ['project-report', report.id] });
    },
  });
}

export function useUpdateProjectReportShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await api.patch(`/project-reports/${id}/share`, payload, { skipProject: true });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-reports'] }),
  });
}

export function useDeleteProjectReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/project-reports/${id}`, { skipProject: true });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-reports'] }),
  });
}
